import React, { useState, useEffect } from 'react';
import { shopService } from '../services/shopService';
import { Shop } from '../types';
import { useShop } from '../contexts/ShopContext';
import { formatDateTime } from '../utils/dateUtils';

interface ShopDetailsTabProps {
  shopId: string;
}

const ShopDetailsTab: React.FC<ShopDetailsTabProps> = ({ shopId }) => {
  const { currentShop, currentMembership, refreshShops } = useShop();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [businessInfo, setBusinessInfo] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);

  // Load shop details
  useEffect(() => {
    loadShopDetails();
  }, [shopId]);

  const loadShopDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const shop = await shopService.getShopById(shopId);
      setName(shop.name || '');
      setDescription(shop.description || '');
      setBusinessInfo(shop.businessInfo || '');
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading shop details:', error);
      setError('Failed to load shop details');
    } finally {
      setLoading(false);
    }
  };

  // Track changes
  useEffect(() => {
    if (currentShop && !loading) {
      const changed =
        name !== (currentShop.name || '') ||
        description !== (currentShop.description || '') ||
        businessInfo !== (currentShop.businessInfo || '');
      setHasChanges(changed);
    }
  }, [name, description, businessInfo, currentShop, loading]);

  const handleSave = async () => {
    if (!hasChanges) return;

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      await shopService.updateShop(shopId, {
        name: name.trim(),
        description: description.trim() || undefined,
        businessInfo: businessInfo.trim() || undefined,
      });

      setSuccessMessage('Shop details updated successfully');
      setHasChanges(false);

      // Refresh shops in context to update the shop selector
      await refreshShops();

      // Auto-dismiss success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Failed to update shop:', error);
      setError(error.message || 'Failed to update shop details');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (currentShop) {
      setName(currentShop.name || '');
      setDescription(currentShop.description || '');
      setBusinessInfo(currentShop.businessInfo || '');
      setHasChanges(false);
      setError('');
      setSuccessMessage('');
    }
  };

  const isDefault = currentMembership?.defaultShop ?? false;

  const handleSetDefault = async () => {
    if (isDefault || settingDefault) return;
    try {
      setSettingDefault(true);
      setError('');
      await shopService.setDefaultShop(shopId);
      setSuccessMessage('This shop is now your default');
      await refreshShops();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Failed to set default shop:', error);
      setError(error.message || 'Failed to set default shop');
    } finally {
      setSettingDefault(false);
    }
  };

  if (loading) {
    return (
      <div className="shop-details-tab-loading">
        <div className="flex items-center justify-center py-12">
          <div className="spinner-sm mr-3"></div>
          <span className="text-muted">Loading shop details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-details-tab">
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h3 className="heading-secondary" style={{ fontSize: '18px', marginBottom: '4px' }}>
          Shop Information
        </h3>
        <p className="text-muted" style={{ fontSize: '14px' }}>
          Manage your shop's basic information and settings
        </p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div
          className="card"
          style={{
            backgroundColor: 'var(--success-bg)',
            borderColor: 'var(--success)',
            marginBottom: '24px',
            padding: '12px 16px'
          }}
        >
          <div style={{ color: 'var(--success-dark)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>✓</span>
            <span style={{ fontSize: '14px' }}>{successMessage}</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="card" style={{ marginBottom: '24px', padding: '12px 16px', backgroundColor: 'var(--error-bg)', borderColor: 'var(--error)' }}>
          <div style={{ color: 'var(--error-text)', fontSize: '14px' }}>{error}</div>
        </div>
      )}

      {/* Shop Details Form */}
      <div className="card">
        <div style={{ padding: '16px' }}>
          {/* Shop Name */}
          <div style={{ marginBottom: '24px' }}>
            <label
              htmlFor="shopName"
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--text-primary)',
                marginBottom: '8px'
              }}
            >
              Shop Name *
            </label>
            <input
              id="shopName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border-light)',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)'
              }}
              placeholder="Enter shop name"
            />
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {name.length}/100 characters
            </div>
          </div>

          {/* Shop Description */}
          <div style={{ marginBottom: '24px' }}>
            <label
              htmlFor="shopDescription"
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--text-primary)',
                marginBottom: '8px'
              }}
            >
              Description
            </label>
            <textarea
              id="shopDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={4}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border-light)',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
              placeholder="Enter a brief description of your shop"
            />
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {description.length}/500 characters
            </div>
          </div>

          {/* Business Info */}
          {currentShop && (currentShop.type === 'BUSINESS' || currentShop.type === 'BUSINESS_PLUS') && (
            <div style={{ marginBottom: '24px' }}>
              <label
                htmlFor="businessInfo"
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  marginBottom: '8px'
                }}
              >
                Business Information
              </label>
              <textarea
                id="businessInfo"
                value={businessInfo}
                onChange={(e) => setBusinessInfo(e.target.value)}
                maxLength={1000}
                rows={6}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
                placeholder="Help Carousel Shop AI Platform users understand your shop. Do not include business or personal sensitive information."
              />
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                {businessInfo.length}/1000 characters
              </div>
            </div>
          )}

          {/* Shop Type (Read-only) */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--text-primary)',
                marginBottom: '8px'
              }}
            >
              Shop Type
            </label>
            <div
              style={{
                padding: '10px 12px',
                border: '1px solid var(--border-light)',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-secondary)'
              }}
            >
              {currentShop?.type || 'Unknown'}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              Contact support to upgrade or change your shop type
            </div>
          </div>

          {/* Default Shop Toggle */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--text-primary)',
                marginBottom: '8px'
              }}
            >
              Default Shop
            </label>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <button
                onClick={handleSetDefault}
                disabled={isDefault || settingDefault}
                style={{
                  position: 'relative',
                  width: '44px',
                  height: '24px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: isDefault ? 'var(--success)' : 'var(--border-medium)',
                  cursor: isDefault ? 'default' : settingDefault ? 'wait' : 'pointer',
                  transition: 'background-color 0.2s ease',
                  padding: 0,
                  opacity: settingDefault ? 0.6 : 1
                }}
                aria-label={isDefault ? 'This is your default shop' : 'Set as default shop'}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '2px',
                    left: isDefault ? '22px' : '2px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--bg-card)',
                    transition: 'left 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
                  }}
                />
              </button>
              <span style={{ fontSize: '14px', color: isDefault ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                {settingDefault ? 'Setting...' : isDefault ? 'This is your default shop' : 'Make this my default shop'}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {isDefault
                ? 'This shop is automatically selected when you log in'
                : 'Set this shop as the one selected when you log in'}
            </div>
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '12px',
              paddingTop: '16px',
              borderTop: '1px solid var(--border-light)',
              flexWrap: 'wrap'
            }}
          >
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving || !name.trim()}
              className="btn btn-primary"
              style={{
                opacity: !hasChanges || saving || !name.trim() ? 0.5 : 1,
                cursor: !hasChanges || saving || !name.trim() ? 'not-allowed' : 'pointer',
                flex: '1 1 auto',
                minWidth: '120px',
                fontSize: '14px',
                padding: '10px 16px'
              }}
            >
              {saving ? (
                <>
                  <span className="spinner-sm" style={{ marginRight: '8px' }}></span>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
            <button
              onClick={handleCancel}
              disabled={!hasChanges || saving}
              className="btn btn-ghost"
              style={{
                opacity: !hasChanges || saving ? 0.5 : 1,
                cursor: !hasChanges || saving ? 'not-allowed' : 'pointer',
                flex: '1 1 auto',
                minWidth: '120px',
                fontSize: '14px',
                padding: '10px 16px'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Shop Metadata */}
      {currentShop && (
        <div className="card" style={{ marginTop: '24px' }}>
          <div style={{ padding: '16px' }}>
            <h4 className="heading-secondary" style={{ fontSize: '16px', marginBottom: '16px' }}>
              Shop Metadata
            </h4>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '16px'
              }}
            >
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  Status
                </div>
                <div>
                  <span
                    className={`status-badge ${currentShop.active ? 'active' : 'inactive'}`}
                    style={{ fontSize: '11px', padding: '2px 6px' }}
                  >
                    {currentShop.active ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  Created
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                  {formatDateTime(currentShop.createdAt)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  Last Updated
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                  {formatDateTime(currentShop.updatedAt)}
                </div>
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Shop ID
              </div>
              <div style={{ fontSize: '14px', color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                {currentShop.id}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopDetailsTab;
